import React from "react";
import { View, Text, Image } from "@react-pdf/renderer";
import "./assets/fonts";
import { artUnitPt, platesPt } from "./layout/artGeometryPt";
import { DEFAULT_PLATES } from "./artPlates";
import { autographLineText, gradeLineText } from "./gradeHelpers";
import { conditionLabel } from "../../extendedArt/conditions";
import { isExtendedArt } from "../../extendedArt/kind";

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

/** Branco sobre placa escura, preto sobre placa clara. */
const textColorFor = (tone) => (tone === "light" ? "#000000" : "#FFFFFF");

/** Retângulo absoluto (topo-esquerdo da página) — slot + platesPt(geometry). */
const absBox = (slot, box) => ({
  left: slot.x + box.x,
  top: slot.y + box.y,
  width: box.width,
  height: box.height,
});

/** Placa semitransparente: preta 0,55 ou branca 0,70, cantos arredondados. */
const PlateBackground = ({ box, tone }) => (
  <View
    style={{
      position: "absolute",
      left: box.left,
      top: box.top,
      width: box.width,
      height: box.height,
      backgroundColor: tone === "light" ? "#FFFFFF" : "#000000",
      opacity: tone === "light" ? 0.7 : 0.55,
      borderRadius: 3,
    }}
  />
);

/**
 * Texto de uma placa: caixa com a mesma posição/tamanho da placa, mas fora
 * dela (sem herdar a opacidade), centralizado verticalmente via flex.
 */
const PlateText = ({ box, tone, fontFamily, fontSize, align = "flex-start", children }) => {
  if (isBlank(children)) return null;
  return (
    <View
      style={{
        position: "absolute",
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: align,
        paddingLeft: align === "flex-start" ? 2 : 0,
      }}
    >
      <Text
        maxLines={1}
        style={{ fontFamily, fontSize, color: textColorFor(tone) }}
      >
        {String(children)}
      </Text>
    </View>
  );
};

/** Placa da nota: condição no estendido (D5), nota geral no comum. */
export const gradeText = (card) => (isExtendedArt(card) ? card.condition : card.notaGeral);

/**
 * Linha da placa "subgrades": no estendido, nome da condição + autógrafo;
 * no comum, subgrades reais (with_subgrades) ou a linha do layout simples
 * legado (AUTHENTIC ou a nota) — mesmo critério de SimpleLabel/SimpleFields.
 */
export const subgradesLine = (card) => {
  if (isExtendedArt(card)) {
    const label = conditionLabel(card.condition);
    const auto = autographLineText(card.notaAutografo);
    return auto ? `${label} · ${auto}` : label;
  }
  if (card.scenario === "with_subgrades") {
    return `S ${card.notaSurface} · C ${card.notaCenter} · E ${card.notaEdge} · Co ${card.notaCorner}`;
  }
  const showAuthentic = card.scenario === "authentic" || card.scenario === "authentic_auto";
  return showAuthentic ? "AUTHENTIC" : gradeLineText(card);
};

export const metaLine = (card) => `${card.edition || ""} · ${card.idioma || ""} · ${card.serial}`;

/**
 * Overlay do papel "art": 4 placas semitransparentes na frente (nome, nota,
 * subgrades/cenário simples, meta) e QR + marca no verso (rotacionado 180°,
 * mesmo critério do QR do layout legado em LabelTagPdf — verso sai de cabeça
 * para baixo antes da dobra). A arte de fundo (PNG aprovado) é carimbada por
 * composeSheetWithUnits; este componente só desenha o overlay transparente.
 */
const ArtExtendedLabel = ({ card, slot, geometry, qrDataUrl }) => {
  if (!card || !slot) return null;

  const plates = { ...DEFAULT_PLATES, ...(card.art?.plates || {}) };
  const boxes = platesPt(geometry);
  const { back } = artUnitPt(geometry);

  const nameBox = absBox(slot, boxes.name);
  const gradeBox = absBox(slot, boxes.grade);
  const subgradesBox = absBox(slot, boxes.subgrades);
  const metaBox = absBox(slot, boxes.meta);
  const backBox = absBox(slot, back);

  return (
    <>
      <PlateBackground box={nameBox} tone={plates.name} />
      <PlateBackground box={gradeBox} tone={plates.grade} />
      <PlateBackground box={subgradesBox} tone={plates.subgrades} />
      <PlateBackground box={metaBox} tone={plates.meta} />

      <PlateText box={nameBox} tone={plates.name} fontFamily="Montserrat-Bold" fontSize={10}>
        {card.nome}
      </PlateText>
      <PlateText
        box={gradeBox}
        tone={plates.grade}
        fontFamily="Montserrat-Bold"
        fontSize={24}
        align="center"
      >
        {gradeText(card)}
      </PlateText>
      <PlateText
        box={subgradesBox}
        tone={plates.subgrades}
        fontFamily="Montserrat-Regular"
        fontSize={7}
      >
        {subgradesLine(card)}
      </PlateText>
      <PlateText box={metaBox} tone={plates.meta} fontFamily="Montserrat-Regular" fontSize={6.5}>
        {metaLine(card)}
      </PlateText>

      <View
        style={{
          position: "absolute",
          left: backBox.left,
          top: backBox.top,
          width: backBox.width,
          height: backBox.height,
          transform: "rotate(180deg)",
        }}
      >
        <View
          style={{
            width: backBox.width,
            height: backBox.height,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {qrDataUrl ? <Image src={qrDataUrl} style={{ width: 40, height: 40 }} /> : null}
          <Text
            maxLines={1}
            style={{ fontFamily: "Montserrat-Bold", fontSize: 8, color: "#000000", marginTop: 2 }}
          >
            MANAFIX
          </Text>
          <Text maxLines={1} style={{ fontFamily: "Montserrat-Regular", fontSize: 6, color: "#000000" }}>
            {card.serial}
          </Text>
          <Text maxLines={1} style={{ fontFamily: "Montserrat-Regular", fontSize: 5, color: "#000000" }}>
            {`v${card.art?.version ?? ""}`}
          </Text>
        </View>
      </View>
    </>
  );
};

export default ArtExtendedLabel;
